import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';

function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; }
  catch { return null; }
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'x-worker-token': getToken(), ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function getYouTubeEmbedUrl(url) {
  try {
    const u = new URL(url);
    let vid = u.searchParams.get('v');
    if (!vid && u.hostname === 'youtu.be') vid = u.pathname.slice(1);
    if (!vid && u.pathname.includes('/embed/')) vid = u.pathname.split('/embed/')[1];
    return vid ? `https://www.youtube.com/embed/${vid}` : null;
  } catch { return null; }
}

export function SignaturePad({ onSave, onCancel }) {
  const canvasRef = useRef();
  const drawing = useRef(false);
  const last = useRef(null);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  }

  function start(e) {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e, canvasRef.current);
  }

  function move(e) {
    e.preventDefault();
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e, canvasRef.current);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();
    last.current = pos;
  }

  function stop(e) {
    e.preventDefault();
    drawing.current = false;
  }

  function clear() {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
  }

  function save() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    const hasContent = data.some((v, i) => i % 4 === 3 && v > 0);
    if (!hasContent) return alert('Please sign before submitting.');
    onSave(canvas.toDataURL('image/png'));
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: '#fff', borderRadius: '16px 16px 0 0', padding: 24, width: '100%' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 700 }}>Sign to Confirm</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#6b7280' }}>By signing, you confirm you have reviewed this safety item.</p>
        <canvas
          ref={canvasRef}
          width={Math.min(window.innerWidth - 48, 480)}
          height={160}
          onMouseDown={start} onMouseMove={move} onMouseUp={stop}
          onTouchStart={start} onTouchMove={move} onTouchEnd={stop}
          style={{ border: '1.5px solid #e5e7eb', borderRadius: 10, display: 'block', touchAction: 'none', background: '#fafafa', width: '100%' }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={clear} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Clear</button>
          <button onClick={onCancel} style={{ flex: 1, padding: 12, borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} style={{ flex: 2, padding: 12, borderRadius: 10, border: 'none', background: '#e87722', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>Submit</button>
        </div>
      </div>
    </div>
  );
}

function SafetyItemViewer({ assignment, onComplete, onBack }) {
  const [showSig, setShowSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const serverBase = API_BASE.replace('/api', '');

  const embedUrl = assignment.type === 'video' ? getYouTubeEmbedUrl(assignment.url) : null;
  const pdfUrl = assignment.type === 'pdf'
    ? (assignment.url.startsWith('http') ? assignment.url : `${serverBase}${assignment.url}`)
    : null;

  async function handleSign(sig) {
    setShowSig(false);
    setSubmitting(true);
    try {
      await apiFetch(`/safety/assignments/${assignment.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature: sig }),
      });
      setDone(true);
      setTimeout(() => onComplete(assignment.id), 1200);
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12, padding: 32 }}>
        <div style={{ fontSize: 56 }}>✅</div>
        <p style={{ fontWeight: 700, fontSize: 18, margin: 0 }}>Marked Complete</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: 'env(safe-area-inset-top, 14px) 16px 14px', paddingTop: 'max(env(safe-area-inset-top), 14px)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 0, color: '#374151' }}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{assignment.title}</div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>Due {new Date(String(assignment.due_date).slice(0,10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, padding: 16 }}>
        {assignment.description && (
          <p style={{ fontSize: 14, color: '#4b5563', margin: '0 0 16px', background: '#fff', padding: 14, borderRadius: 10, border: '1px solid #e5e7eb' }}>
            {assignment.description}
          </p>
        )}

        {assignment.type === 'video' && embedUrl && (
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 12, overflow: 'hidden', background: '#000' }}>
            <iframe
              src={embedUrl}
              title={assignment.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </div>
        )}

        {assignment.type === 'video' && !embedUrl && (
          <a href={assignment.url} target="_blank" rel="noreferrer"
            style={{ display: 'block', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, textAlign: 'center', color: '#e87722', fontWeight: 600, textDecoration: 'none' }}>
            Open Video ↗
          </a>
        )}

        {assignment.type === 'pdf' && pdfUrl && (
          <iframe
            src={pdfUrl}
            title={assignment.title}
            style={{ width: '100%', height: '60vh', border: 'none', borderRadius: 12, background: '#fff' }}
          />
        )}
      </div>

      {/* Footer */}
      <div style={{ background: '#fff', borderTop: '1px solid #e5e7eb', padding: 16 }}>
        <button
          onClick={() => setShowSig(true)}
          disabled={submitting}
          style={{ width: '100%', padding: 16, borderRadius: 12, border: 'none', background: '#e87722', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
          {submitting ? 'Submitting…' : 'Mark Complete & Sign'}
        </button>
      </div>

      {showSig && <SignaturePad onSave={handleSign} onCancel={() => setShowSig(false)} />}
    </div>
  );
}

// Read-and-sign a safety document (reuses the same SignaturePad as training).
function DocSigner({ doc, onSigned, onBack }) {
  const [showSig, setShowSig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const serverBase = API_BASE.replace('/api', '');
  const url = doc.filename ? `${serverBase}/uploads/${doc.filename}` : null;
  const isPdf = (doc.mime_type || '').includes('pdf');

  async function handleSign(sig) {
    setShowSig(false); setSubmitting(true);
    try {
      await apiFetch(`/safety-documents/${doc.id}/sign`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ signature: sig }) });
      setDone(true); setTimeout(() => onSigned(doc.id), 1200);
    } catch (e) { alert('Error: ' + e.message); } finally { setSubmitting(false); }
  }

  if (done) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 12, padding: 32 }}>
      <div style={{ fontSize: 56 }}>✅</div><p style={{ fontWeight: 700, fontSize: 18, margin: 0 }}>Signed</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', paddingTop: 'max(env(safe-area-inset-top), 14px)', paddingBottom: 14, paddingLeft: 16, paddingRight: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 0, color: '#374151' }}>←</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</div>
          {doc.category && <div style={{ fontSize: 12, color: '#9ca3af' }}>{doc.category}</div>}
        </div>
      </div>
      <div style={{ flex: 1, padding: 16 }}>
        {doc.description && <p style={{ fontSize: 14, color: '#4b5563', margin: '0 0 16px', background: '#fff', padding: 14, borderRadius: 10, border: '1px solid #e5e7eb' }}>{doc.description}</p>}
        {doc.body && <div style={{ fontSize: 14, color: '#1f2937', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: '#fff', padding: 16, borderRadius: 12, border: '1px solid #e5e7eb', marginBottom: 16 }}>{doc.body}</div>}
        {url ? (isPdf
          ? <iframe src={url} title={doc.title} style={{ width: '100%', height: '62vh', border: 'none', borderRadius: 12, background: '#fff' }} />
          : <img src={url} alt={doc.title} style={{ width: '100%', borderRadius: 12, border: '1px solid #e5e7eb' }} />
        ) : (!doc.body && <p style={{ color: '#9ca3af' }}>No file attached.</p>)}
      </div>
      <div style={{ background: '#fff', borderTop: '1px solid #e5e7eb', padding: 16 }}>
        <button onClick={() => setShowSig(true)} disabled={submitting}
          style={{ width: '100%', padding: 16, borderRadius: 12, border: 'none', background: '#e87722', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>
          {submitting ? 'Submitting…' : 'I have read this — Sign'}
        </button>
      </div>
      {showSig && <SignaturePad onSave={handleSign} onCancel={() => setShowSig(false)} />}
    </div>
  );
}

export default function SafetyTraining() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const docsRef = useRef(null);
  const [pending, setPending] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);

  function loadAll() {
    setLoading(true); setLoadErr(false);
    Promise.all([apiFetch('/safety/pending'), apiFetch('/safety/completed'), apiFetch('/safety-documents/mine').catch(() => [])])
      .then(([p, c, d]) => { setPending(Array.isArray(p) ? p : []); setCompleted(Array.isArray(c) ? c : []); setDocs(Array.isArray(d) ? d : []); })
      .catch(() => setLoadErr(true))
      .finally(() => setLoading(false));
  }
  useEffect(() => { loadAll(); }, []);

  const unsignedDocs = docs.filter(d => !d.signed && d.requires_sign);

  // Arriving from the Safety Documents tile — jump to the documents section.
  useEffect(() => {
    if (!loading && searchParams.get('focus') === 'documents' && docsRef.current) {
      docsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, searchParams]);

  if (viewingDoc) {
    return <DocSigner doc={viewingDoc} onBack={() => setViewingDoc(null)}
      onSigned={id => { setDocs(prev => prev.map(d => d.id === id ? { ...d, signed: true } : d)); setViewingDoc(null); }} />;
  }

  if (viewing) {
    return (
      <SafetyItemViewer
        assignment={viewing}
        onBack={() => setViewing(null)}
        onComplete={id => {
          const item = pending.find(p => p.id === id);
          setPending(prev => prev.filter(p => p.id !== id));
          if (item) setCompleted(prev => [{ ...item, completed_at: new Date().toISOString() }, ...prev]);
          setViewing(null);
        }}
      />
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', paddingTop: 'max(env(safe-area-inset-top), 14px)', paddingBottom: 14, paddingLeft: 16, paddingRight: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', padding: 0, color: '#374151' }}>←</button>
        <div>
          <div style={{ fontWeight: 700, fontSize: 17 }}>Safety Training</div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>{pending.length} item{pending.length !== 1 ? 's' : ''} pending</div>
        </div>
      </div>

      <div style={{ padding: 16, maxWidth: 540, margin: '0 auto' }}>
        {loading ? (
          <p style={{ color: '#9ca3af', textAlign: 'center', paddingTop: 40 }}>Loading…</p>
        ) : loadErr ? (
          <div style={{ textAlign: 'center', paddingTop: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <p style={{ fontWeight: 700, fontSize: 16, margin: '0 0 6px' }}>Couldn't load your safety items</p>
            <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 16px' }}>Check your connection and try again.</p>
            <button onClick={loadAll} style={{ background: '#e87722', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 24px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
          </div>
        ) : (
          <>
            {pending.length === 0 && unsignedDocs.length === 0 && (
              <div style={{ textAlign: 'center', paddingTop: 48 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                <p style={{ fontWeight: 700, fontSize: 17, margin: '0 0 6px' }}>All caught up!</p>
                <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>No pending safety requirements.</p>
              </div>
            )}

            {unsignedDocs.length > 0 && (
              <div ref={docsRef} style={{ marginBottom: 18, scrollMarginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>Documents to Sign</div>
                {unsignedDocs.map(d => (
                  <button key={d.id} onClick={() => setViewingDoc(d)}
                    style={{ width: '100%', background: '#fff', border: '1.5px solid #fed7aa', borderRadius: 12, padding: 16, textAlign: 'left', cursor: 'pointer', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 16 }}>📄</span>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{d.title}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#c2410c' }}>Requires your signature{d.category ? ` · ${d.category}` : ''}</div>
                    </div>
                    <span style={{ color: '#e87722', fontSize: 18 }}>→</span>
                  </button>
                ))}
              </div>
            )}

            {pending.map(item => (
              <button key={item.id} onClick={() => setViewing(item)}
                style={{ width: '100%', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, textAlign: 'left', cursor: 'pointer', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16 }}>{item.type === 'video' ? '▶' : '📄'}</span>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{item.title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    Due {new Date(String(item.due_date).slice(0,10) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                  {item.description && <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>{item.description}</div>}
                </div>
                <span style={{ color: '#e87722', fontSize: 18 }}>→</span>
              </button>
            ))}

            {/* Completed section */}
            {completed.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <button onClick={() => setShowCompleted(v => !v)}
                  style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 10 }}>
                  {showCompleted ? '▲' : '▼'} {completed.length} completed
                </button>
                {showCompleted && completed.map(item => (
                  <div key={item.id}
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: 0.7 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                        <span style={{ fontSize: 14 }}>✅</span>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{item.title}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af' }}>
                        Completed {new Date(item.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
